import { Formik, useFormikContext } from 'formik'
import * as React from 'react'
import { ReactElement, useContext, useEffect, useMemo, useState } from 'react'
import Form from '../Form'
import { Pivot, PivotItem, Stack, StackItem } from '@fluentui/react'
import { FileAction, FileMutationDto } from '../../../config/fileConfig'
import FinancePivotItem from './FinancePivotItem'
import { FormikConfig } from 'formik/dist/types'
import HeaderCard from './HeaderCard'
import GeneralPivotItem from './GeneralPivotItem'
import { AxiosResponse } from 'axios'
import { useHistory } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import FileContext from '../../../context/FileContext'
import * as Yup from 'yup'
import { DriveItem } from '@microsoft/microsoft-graph-types'
import { DRIVE_ITEM_CONFIG } from '../../TextField/Folder/FolderField'
import useAxios from '../../../hooks/useAxios'
import AuthorizationPivotItem from './AuthorizationPivotItem'
import ModulePivotItem, { ModulesConstants } from './ModulePivotItem'
import { Accounting, ModuleType, UnitDto } from '../../../context/GlobalContext'
import useModules from '../../../hooks/module/useModules'
import useViewport, { Viewport } from '../../../hooks/useViewport'
import { useSettings } from '../../../hooks/useSettings'
import { EntityType } from '../Accounting/EntityField'
import useHost from '../../../hooks/useHost'
import { createDimension } from '../Accounting/CreateDimensionDialog'
import useUnits from '../../../hooks/useUnits'
import { AccountingId } from '../../../config/finance/invoiceConfig'
import { makeStyles, MessageBar, tokens } from '@fluentui/react-components'
import AttributesList from '../Attributes/AttributesList'
import { usePrevious } from '@fluentui/react-hooks'
import FormikFormContainer from '../../../components/Form/FormContainer'
import { useTranslation } from 'react-i18next'
import {
    AttributeContextType,
    AttributeDefinitionDto,
    AttributesReturnProps,
    AttributeViewType,
    EntityAttributeType,
    getAttributeValidationSchema,
} from '../../../hooks/useAttributes'
import { formatMultipleEmailAddresses, yupEmailRegex } from '../../../helpers/regexHelper'

export enum FileFormType {
    Add = 'ADD',
    Edit = 'EDIT',
}

export type PivotProps = {
    formType: FileFormType
    orientation: 'horizontal' | 'vertical'
    id?: string
}

type Props = Omit<PivotProps, 'orientation'> & {
    id?: string
    formikProps: Omit<FormikConfig<FileMutationDto>, 'onSubmit'>
    onSubmit: (file: FileMutationDto) => Promise<AxiosResponse<string>>
    attributes: AttributesReturnProps
}

type OnSubmit = {
    (props: Props): FormikConfig<FileMutationDto>['onSubmit']
}

type FormContainerProps = PivotProps & {
    setOnSubmit: (onSubmit: () => OnSubmit) => void
    attributes: AttributesReturnProps
}

const getValidationSchema = (
    translate: (key: string, values?: any) => string,
    attributeDefinitions: AttributeDefinitionDto[],
): Yup.ObjectSchema<any> => {
    return Yup.object().shape({
        reference: Yup.string().label(translate('file.edit.form.header.reference')).required(),
        name: Yup.string().label(translate('file.edit.form.header.name')).required(),
        ownerId: Yup.string().label(translate('file.edit.form.general.ownerId')).uuid().required(),
        language: Yup.string().label(translate('file.edit.form.general.language')).required(),
        status: Yup.string().label(translate('file.edit.form.general.status')).required(),
        invoiceEmail: Yup.string()
            .nullable()
            .label(translate('file.edit.form.finance.invoiceemail'))
            .test({
                message: translate('base.form.email.warning'),
                test: values =>
                    !values ||
                    formatMultipleEmailAddresses(values)?.every(value => {
                        const result = value?.trim().match(yupEmailRegex)
                        return result?.length > 0
                    }),
            }),
        attributes: getAttributeValidationSchema(translate, attributeDefinitions),
    })
}

const useStyles = makeStyles({
    alert: {
        marginLeft: tokens.spacingHorizontalS,
        marginRight: tokens.spacingHorizontalS,
    },
})

function FormContainer(props: FormContainerProps): ReactElement {
    const axios = useAxios()
    const history = useHistory()
    const styles = useStyles()
    const { RowKey: hostKey } = useHost()
    const dispatch = useDispatch()
    const { settings } = useSettings()
    const { t: translate } = useTranslation()

    const { initialValues, values, setFieldValue, status, setStatus } = useFormikContext<FileMutationDto>()
    const previousValues = usePrevious<FileMutationDto>(values)
    const { units } = useUnits()
    const unit = useMemo<UnitDto>(() => units.find(unit => unit.key === values.unit), [units, values.unit])
    const { resetFiles } = useContext(FileContext)
    const [selectedPivot, setSelectedPivot] = useState<string>('general')
    const modules = useModules()

    const accountingCostUnit = useMemo<boolean>(
        () =>
            modules.has(ModuleType.Accounting) &&
            unit?.enabled &&
            unit?.accounting === Accounting.Exact &&
            (unit?.dimensions || []).includes(EntityType.CostUnit),
        [unit, values.unit],
    )
    const accountingProject = useMemo<boolean>(
        () =>
            modules.has(ModuleType.Accounting) &&
            unit?.enabled &&
            unit.accounting === Accounting.Twinfield &&
            (unit.dimensions || []).includes(EntityType.Project),
        [unit, values.unit],
    )

    //Apply onAfterChecked for each enabled modules to ensure both Add and Edit have all fields set correctly
    useEffect(() => {
        if (values.modules && values.modules.length > 0) {
            values.modules.forEach(module => ModulesConstants.get(module.type)?.onAfterChecked?.(initialValues, settings, setFieldValue))
        }
    }, [])

    useEffect(() => {
        if (props.formType === FileFormType.Edit) {
            return
        }
        if (!values.accounting) {
            values.accounting = {}
        }
        //Exact
        if (accountingCostUnit) {
            values.accounting[unit.key] = {
                costUnit: {
                    id: '',
                    code: values.reference,
                    name: values.name,
                    unit: unit.key,
                },
            }
            setStatus({ ...status, accountingCostUnit: true })
        } else if (previousValues?.unit) {
            values.accounting[previousValues?.unit] = {
                ...values.accounting[previousValues?.unit],
                costUnit: undefined,
            }
        }
        if (accountingProject) {
            values.accounting[unit.key] = {
                project: {
                    id: '',
                    code: undefined,
                    name: values.name,
                    unit: unit.key,
                },
            }
            setStatus({ ...status, accountingProject: true })
        } else if (previousValues?.unit) {
            values.accounting[previousValues?.unit] = {
                ...values.accounting[previousValues?.unit],
                project: undefined,
            }
        }
    }, [unit, values.unit, values.reference, values.name])

    useEffect(() => {
        props.setOnSubmit(() => (props: Props) => async (file: FileMutationDto, helpers) => {
            if (props.formType === FileFormType.Add) {
                const exists = await axios.get<string>(`/file/reference-check/${file.reference}`)
                if (exists.data?.length > 0) {
                    helpers.setFieldError('reference', translate('file.edit.form.error.reference.exists', { suggestion: exists.data }))
                    return Promise.reject()
                }
            }

            if (file.documentRootId) {
                const exists = await axios.get<string>(`/file/document-folder-check/${file.reference}`, {
                    params: {
                        location: file.documentRootId,
                    },
                })
                if (exists.data?.length > 0) {
                    helpers.setFieldError('documentRootId', translate('file.edit.form.error.sharepoint.exists', { exists: exists.data }))
                    return Promise.reject()
                }
            }

            if (status?.documentRootPath) {
                let response: AxiosResponse<DriveItem>
                let lastPathId = undefined
                let currentPath = ''

                const fragments: string[] = status.documentRootPath.split('/')
                while (fragments.length) {
                    const fragment = fragments.shift()
                    currentPath = currentPath + '/' + fragment

                    response = await axios
                        .get<DriveItem>('/root:' + currentPath, DRIVE_ITEM_CONFIG)
                        .then(response => response)
                        .catch(() => undefined)
                    if (fragments.length && response?.data?.id) {
                        //where it's not the file's folder and the folder exists.
                        lastPathId = response.data.id
                    } else if (fragments.length) {
                        //where it's not the file's folder.
                        response = await axios.post<DriveItem>(
                            !lastPathId ? '/root/children' : `/items/${lastPathId}/children`,
                            { name: fragment, folder: {}, '@microsoft.graph.conflictBehavior': 'fail' },
                            DRIVE_ITEM_CONFIG,
                        )
                        lastPathId = response.data.id
                    } else if (file.documentRootId) {
                        //if the file's folder was created before we need to move&rename it.
                        response = await axios.patch<DriveItem>(
                            `/items/${file.documentRootId}`,
                            { name: fragment, ...(lastPathId ? { parentReference: { id: lastPathId } } : {}) },
                            DRIVE_ITEM_CONFIG,
                        )
                        lastPathId = response.data.id
                    } else {
                        //if the file's folder was not created before.
                        response = await axios.post<DriveItem>(
                            !lastPathId ? '/root/children' : `/items/${lastPathId}/children`,
                            { name: fragment, folder: {}, '@microsoft.graph.conflictBehavior': 'fail' },
                            DRIVE_ITEM_CONFIG,
                        )
                        lastPathId = response.data.id
                    }
                }

                if (!lastPathId) {
                    helpers.setFieldError('documentRootId', translate('file.edit.form.error.sharepoint'))
                    return Promise.reject()
                }

                file.documentRootId = lastPathId
            }

            /**
             * Automatically create CostUnit in Exact.
             */
            if (status?.accountingCostUnit && unit && values.accounting?.[values.unit]?.costUnit && !values.accounting[values.unit].costUnit.id) {
                const response: AxiosResponse<AccountingId> = await createDimension(axios, EntityType.CostUnit, hostKey, values.unit, {
                    code: values.accounting[values.unit].costUnit.code,
                    name: values.accounting[values.unit].costUnit.name,
                })
                if (!response) {
                    helpers.setFieldError('unit', translate('file.edit.form.error.accounting.costunit', { unit_key: unit.name }))
                    return Promise.reject()
                }
                values.accounting[values.unit].costUnit = response.data
            }
            if (status?.accountingProject && values.accounting?.[values.unit]?.project && !values.accounting[values.unit].project.id) {
                const response: AxiosResponse<AccountingId> = await createDimension(axios, EntityType.Project, hostKey, values.unit, {
                    code: values.accounting[values.unit].project.code,
                    name: values.accounting[values.unit].project.name,
                })
                if (!response) {
                    helpers.setFieldError('unit', translate('file.edit.form.error.accounting.project', { unit_key: unit.name }))
                    return Promise.reject()
                }
                values.accounting[values.unit].project = response.data
            }

            return props.onSubmit(file).then(({ data: id }: AxiosResponse<string>) => {
                history.push(`/file/${id}/party`)
                dispatch({ type: FileAction.ResetCurrent })
                dispatch({ type: FileAction.ResetList })
                resetFiles()
            })
        })
    }, [status])

    const attributeItems = useMemo(
        () =>
            modules.has(ModuleType.Attributes) && (props.attributes.activeAttributeDefinitions.length > 0 || values.attributes?.length > 0)
                ? props.attributes.attributeCategories?.map((category, index) => (
                      <PivotItem key={index} itemKey={`attributes-${index}`} headerText={category || translate('file.edit.form.attributes.label')}>
                          <FormikFormContainer>
                              <AttributesList
                                  {...props}
                                  entity={values}
                                  entityId={props.id}
                                  entityType={EntityAttributeType.File}
                                  context={[AttributeContextType.File]}
                                  viewType={AttributeViewType.Input}
                                  category={category}
                                  setFieldValue={setFieldValue}
                              />
                          </FormikFormContainer>
                      </PivotItem>
                  ))
                : [],
        [modules, props.attributes.attributeCategories],
    )

    const pivotItems = useMemo(() => {
        return [
            <PivotItem key={'general'} itemKey="general" headerText={translate('file.edit.form.general.label')}>
                <GeneralPivotItem {...props} />
            </PivotItem>,
            modules.has(ModuleType.Authorization) && (
                <PivotItem key={'authorization'} itemKey="authorization" headerText={translate('file.edit.form.authorization.label')}>
                    <AuthorizationPivotItem {...props} />
                </PivotItem>
            ),
            modules.has(ModuleType.Invoices) && (
                <PivotItem key={'finance'} itemKey="finance" headerText={translate('file.edit.form.finance.label')}>
                    <FinancePivotItem {...props} />
                </PivotItem>
            ),
            modules.fileModuleSize() > 0 && (
                <PivotItem key={'modules'} itemKey="modules" headerText={translate('file.edit.form.modules.label')}>
                    <ModulePivotItem {...props} />
                </PivotItem>
            ),
            ...attributeItems,
        ]
    }, [modules, props.attributes.attributeCategories])

    // noinspection com.intellij.reactbuddy.ArrayToJSXMapInspection
    return (
        <Stack tokens={{ childrenGap: 's1' }}>
            <StackItem>
                <HeaderCard id={props.id} formType={props.formType} />
            </StackItem>
            {accountingCostUnit && props.formType === FileFormType.Add && (
                <MessageBar intent="info" className={styles.alert}>
                    {translate('file.edit.form.info.accounting.costunit1')}
                    <br />
                    {translate('file.edit.form.info.accounting.costunit2')}
                </MessageBar>
            )}
            {accountingProject && props.formType === FileFormType.Add && (
                <MessageBar intent="info" className={styles.alert}>
                    {translate('file.edit.form.info.accounting.project1')}
                    <br />
                    {translate('file.edit.form.info.accounting.project2')}
                </MessageBar>
            )}
            <Pivot selectedKey={selectedPivot} onLinkClick={item => setSelectedPivot(item.props.itemKey)}>
                {pivotItems}
            </Pivot>
        </Stack>
    )
}

export default function FileForm(props: Props): ReactElement {
    const [onSubmit, setOnSubmit] = useState<OnSubmit>()
    const { isViewport, viewport } = useViewport()
    const orientation = useMemo(() => (isViewport('>=', Viewport.XXL) ? 'horizontal' : 'vertical'), [viewport])
    const { t: translate } = useTranslation()

    const validationSchema = useMemo(
        () => getValidationSchema(translate, props.attributes.activeAttributeDefinitions),
        [translate, props.attributes.activeAttributeDefinitions],
    )

    return (
        <Formik<FileMutationDto>
            {...props.formikProps}
            validateOnChange={false}
            validateOnBlur={false}
            validationSchema={validationSchema}
            onSubmit={onSubmit?.(props)}
        >
            <Form>
                <FormContainer
                    setOnSubmit={setOnSubmit}
                    id={props.id}
                    formType={props.formType}
                    orientation={orientation}
                    attributes={props.attributes}
                />
            </Form>
        </Formik>
    )
}
